import React from 'react'
import clsx from 'classnames'
import styles from './Tabs.module.scss'

type TabValue = string | number

type TabsContextValue = {
  selected: TabValue | undefined,
  onChange: (value: TabValue) => void,
}

const TabsContext = React.createContext<TabsContextValue>({ selected: undefined, onChange: () => undefined })

type TabRef = { el: HTMLDivElement | null, value: TabValue }

type TabsProps<T extends TabValue> = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  selected: T,
  onChange: (value: T) => void,
  className?: string,
  tabsClassName?: string,
  indicatorClassName?: string,
  /**
   * Instead of moving the indicator under the selected tab, scroll the tabs so the selected one
   * is centred over it; the tabs can also be swiped (used on narrow screens)
   */
  reverseMove?: boolean,
}

const SWIPE_THRESHOLD = 15

/** Tabs with a wavy indicator that follows the selected tab */
export class Tabs<T extends TabValue> extends React.Component<TabsProps<T>> {

  container = React.createRef<HTMLDivElement>()

  indicator = React.createRef<HTMLDivElement>()

  tabs = React.createRef<HTMLDivElement>()

  offset = 0

  touchStartX = 0

  tabsRefs = new Map<number, TabRef>()

  getMovingEl = () => (this.props.reverseMove ? this.tabs.current : this.indicator.current)

  getActiveTab = () => {
    for (const { el, value } of this.tabsRefs.values()) {
      if (value === this.props.selected) {
        return el
      }
    }
    return null
  }

  getEdgeTab = (last: boolean) => {
    const refs = Array.from(this.tabsRefs.values())
    return (last ? refs[refs.length - 1] : refs[0])?.el ?? null
  }

  calcOffset = (activeElement: HTMLElement) => {
    const container = this.container.current as HTMLDivElement
    const tabs = this.tabs.current as HTMLDivElement
    const elHalfWidth = activeElement.clientWidth / 2
    const parentHalfWidth = container.clientWidth / 2
    return Math.round(activeElement.offsetLeft + elHalfWidth - parentHalfWidth + tabs.offsetLeft)
  }

  applyOffset = (offset: number, noTransition = false) => {
    const el = this.getMovingEl()
    if (!el) {
      return
    }
    el.style.transform = `translateX(${offset}px)`
    el.style.transition = noTransition ? 'none' : ''
  }

  move = (noTransition = false) => {
    if (!this.container.current || !this.tabs.current) {
      return
    }
    const activeElement = this.getActiveTab()
    if (!activeElement) {
      return
    }
    this.offset = this.calcOffset(activeElement) * (this.props.reverseMove ? -1 : 1)
    this.applyOffset(this.offset, noTransition)
  }

  applyEdgeOffsets = (newOffset: number) => {
    const first = this.getEdgeTab(false)
    const last = this.getEdgeTab(true)
    if (!first || !last) {
      return newOffset
    }
    return Math.max(-this.calcOffset(last), Math.min(-this.calcOffset(first), newOffset))
  }

  handleTouchStart = (e: TouchEvent) => {
    this.touchStartX = e.touches[0].clientX
  }

  handleTouchMove = (e: TouchEvent) => {
    const delta = e.touches[0].clientX - this.touchStartX
    this.applyOffset(this.applyEdgeOffsets(this.offset + delta), true)
  }

  findTabByOffset = (offset: number, direction: number) => {
    for (const [index, tab] of this.tabsRefs) {
      const prevTab = this.tabsRefs.get(index - direction)
      if (!prevTab?.el || !tab.el) {
        continue
      }
      const tabOffset = this.calcOffset(tab.el)
      const prevTabOffset = this.calcOffset(prevTab.el)
      if (((direction > 0) === (offset > prevTabOffset)) && ((direction > 0) === (offset < tabOffset))) {
        return tab
      }
    }
    return null
  }

  handleTouchEnd = (e: TouchEvent) => {
    const delta = e.changedTouches[0].clientX - this.touchStartX
    const newOffset = this.applyEdgeOffsets(this.offset + delta)
    const actualDelta = this.offset - newOffset
    this.offset = newOffset
    if (actualDelta === 0) {
      return
    }
    if (Math.abs(actualDelta) > SWIPE_THRESHOLD) {
      const tab = this.findTabByOffset(-this.offset, Math.sign(actualDelta))
      if (tab) {
        this.handleChange(tab.value)
      }
    } else {
      this.move()
    }
  }

  applyTouchListeners = () => {
    const tabs = this.tabs.current
    tabs?.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    tabs?.addEventListener('touchmove', this.handleTouchMove, { passive: true })
    tabs?.addEventListener('touchend', this.handleTouchEnd)
  }

  removeTouchListeners = () => {
    const tabs = this.tabs.current
    tabs?.removeEventListener('touchstart', this.handleTouchStart)
    tabs?.removeEventListener('touchmove', this.handleTouchMove)
    tabs?.removeEventListener('touchend', this.handleTouchEnd)
  }

  resetTransform = (el: HTMLDivElement | null) => {
    if (el) {
      el.style.transition = 'none'
      el.style.transform = ''
    }
  }

  componentDidMount() {
    this.move(true)
    if (this.props.reverseMove) {
      this.applyTouchListeners()
    }
  }

  componentDidUpdate(prevProps: TabsProps<T>) {
    if (prevProps.reverseMove && !this.props.reverseMove) {
      this.resetTransform(this.tabs.current)
      this.move(true)
      this.removeTouchListeners()
    } else if (!prevProps.reverseMove && this.props.reverseMove) {
      this.resetTransform(this.indicator.current)
      this.move(true)
      this.applyTouchListeners()
    }
    if (prevProps.selected !== this.props.selected) {
      this.move()
    }
  }

  componentWillUnmount() {
    this.removeTouchListeners()
  }

  handleChange = (value: TabValue) => {
    this.props.onChange(value as T)
  }

  render() {
    const {
      selected, onChange, children, className, tabsClassName, indicatorClassName, reverseMove, ...rest
    } = this.props
    let tabsCounter = 0
    return (
      <div className={clsx([styles.container, className])} {...rest} ref={this.container}>
        <div ref={this.tabs} className={clsx([styles.tabs, tabsClassName])}>
          <TabsContext.Provider value={{ selected, onChange: this.handleChange }}>
            {React.Children.map(children, (child) => {
              if (!React.isValidElement<TabProps>(child) || child.type !== Tab) {
                return child
              }
              const index = tabsCounter++
              const { value } = child.props
              return React.cloneElement(child as React.ReactElement<TabProps & React.RefAttributes<HTMLDivElement>>, {
                ref: (el: HTMLDivElement | null) => {
                  this.tabsRefs.set(index, { el, value })
                },
              })
            })}
          </TabsContext.Provider>
        </div>
        <div className={styles.indicatorContainer}>
          <div ref={this.indicator} className={clsx([styles.indicator, indicatorClassName])} />
        </div>
      </div>
    )
  }
}

type TabProps = React.HTMLAttributes<HTMLDivElement> & {
  value: TabValue,
  selectedClassName?: string,
}

export const Tab = React.forwardRef<HTMLDivElement, TabProps>((props, ref) => {
  const { value, children, className, selectedClassName, ...rest } = props
  const { selected, onChange } = React.useContext(TabsContext)
  const tabClassName = clsx([styles.tab, className, {
    [selectedClassName ?? '']: selectedClassName && selected === value,
  }])
  return (
    <div ref={ref} className={tabClassName} data-value={value} onClick={() => onChange(value)} {...rest}>
      {children}
    </div>
  )
})
Tab.displayName = 'Tab'
